'use client'
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Song } from '../types/types';
import { FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';

interface MusicListProps {
  songs: Song[];
}

const MusicList: React.FC<MusicListProps> = ({ songs }) => {
  const [filter, setFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('title');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const filteredSongs = songs.filter((song) =>
    !song.isDisabled && song.title.toLowerCase().includes(filter.toLowerCase())
  );

  const sortedSongs = [...filteredSongs].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'duration') {
      comparison = (a.duration || 0) - (b.duration || 0);
    } else if (sortBy === 'instrumentation') {
      comparison = (a.instrumentation || '').localeCompare(b.instrumentation || '');
    } else {
      comparison = a.title.localeCompare(b.title);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Group songs by medium
  const groupedSongs = sortedSongs.reduce((acc, song) => {
    const medium = song.medium || 'Other';
    if (!acc[medium]) {
      acc[medium] = [];
    }
    acc[medium].push(song);
    return acc;
  }, {} as Record<string, Song[]>);

  // Sort medium keys alphabetically, but put "Other" at the end
  const sortedMediums = Object.keys(groupedSongs).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  useEffect(() => {
    setSortBy('title');
    setSortOrder('asc');
  }, []);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:py-12 font-goodfont">
      {/* Search and Sort Controls */}
      <div className="mb-12 backdrop-blur-sm bg-black/30 p-4 border border-white/20 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-grow">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by title..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full p-2 pl-10 bg-black/50 text-white border border-white/30 rounded-sm focus:outline-none focus:border-white transition-colors"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 bg-black/50 text-white border border-white/30 rounded-sm focus:outline-none focus:border-white transition-colors"
            >
              <option value="title">Sort by Title</option>
              <option value="duration">Sort by Duration</option>
              <option value="instrumentation">Sort by Instrumentation</option>
            </select>
            <button 
              onClick={toggleSortOrder} 
              className="p-2 bg-black/50 text-white border border-white/30 rounded-sm focus:outline-none hover:border-white transition-colors"
            >
              {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
            </button>
          </div>
        </div>
      </div>

      {/* Grouped Songs */}
      <div className="space-y-16">
        {sortedMediums.map((medium) => (
          <div key={medium}>
            {/* Medium Header */}
            <div className="mb-6">
              <h2 className="text-3xl sm:text-4xl text-white uppercase tracking-widest font-bold">
                {medium}
              </h2>
              <div className="mt-2 h-px bg-gradient-to-r from-white/60 to-transparent" />
            </div>

            {/* Song List */}
            <div className="ml-4 sm:ml-8 backdrop-blur-sm bg-black/30 border-l border-white/20">
              {groupedSongs[medium].map((song) => (
                <Link key={song.id} href={`/music/${song.id}`}>
                  <div className="px-5 py-3 hover:bg-white/10 hover:pl-7 border-l-2 border-l-transparent hover:border-l-white transition-all duration-150 ease-out cursor-pointer group">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="flex items-baseline gap-2">
                        <h3 className="text-sm sm:text-base text-white/80 group-hover:text-white tracking-wide transition-colors duration-150">
                          {song.title}
                        </h3>
                        {song.instrumentation && (
                          <span className="text-xs text-white/40 group-hover:text-white/50 transition-colors duration-150">
                            {song.instrumentation}
                          </span>
                        )}
                      </div>
                      {song.duration && (
                        <span className="text-xs text-white/40 group-hover:text-white/60 tabular-nums transition-colors duration-150">
                          {song.duration} min
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      {sortedSongs.length === 0 && (
        <div className="text-center py-12 backdrop-blur-sm bg-black/30 border border-white/20 rounded-sm">
          <p className="text-white/70 text-lg">No songs found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default MusicList;